import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { getGlobalApiClient } from '../utils/apiClient.js';
import TimeRangeSelector from '../components/TimeRangeSelector';
import DashboardStatistics from '../components/DashboardStatistics';
import CallSuccessRateCard from '../components/CallSuccessRateCard';
import useAnalytics from '../hooks/useAnalytics';
import { getTimeRangeLabel } from '../utils/consolidatedAnalytics.js';

export default function Dashboard() {
  const navigate = useNavigate();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const apiClient = useRef(getGlobalApiClient(getToken)).current;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);
  
  // Analytics hook for live data
  const {
    analytics,
    loading: analyticsLoading,
    error: analyticsError,
    timeRange,
    retry: retryAnalytics,
    changeTimeRange
  } = useAnalytics();



  // Time range options for the selector
  const timeRangeOptions = [
    { value: 'billing_period', label: 'Current Billing Period' },
    { value: '1_day', label: 'Last 24 Hours' },
    { value: '7_days', label: 'Last 7 Days' },
    { value: '30_days', label: 'Last 30 Days' },
    { value: '60_days', label: 'Last 60 Days' },
    { value: '90_days', label: 'Last 90 Days' },
    { value: 'month_to_date', label: 'Month to Date' },
    { value: 'custom', label: 'Custom Range' }
  ];

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!isLoaded) {
        return;
      }
      
      if (!isSignedIn) {
        navigate('/sign-in');
        return;
      }
      
      try {
        const token = await getToken();
        const userData = await apiClient.get('/api/auth/me');
        
        // Check if user has billing preferences set up (wallet-based billing)
        if (!userData.billing_preferences || !userData.billing_preferences.plan_id) {
          navigate('/plans');
          return;
        }

        setUserSubscription({ status: 'active', plan_id: userData.billing_preferences.plan_id });
        setLoading(false);
      } catch (error) {
        setError(`Failed to load dashboard data: ${error.message}`);
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [isLoaded, isSignedIn, apiClient, navigate, getToken]);

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/plans')}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:text-white"
          >
            Go to Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400">Overview of your ParrotMate account and activity</p>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold leading-6 text-gray-900 dark:text-white">
            {analytics ? getTimeRangeLabel(timeRange, analytics.timeRange?.billingDaysRemaining) : 'Analytics'}
          </h3>
          <div className="flex items-center space-x-4">
            <TimeRangeSelector
              value={timeRange}
              onChange={changeTimeRange}
              options={timeRangeOptions}
              loading={analyticsLoading}
            />
          </div>
        </div>
        
        <DashboardStatistics
          analytics={analytics}
          loading={analyticsLoading}
          error={analyticsError}
          onRetry={retryAnalytics}
        />
      </div>

      {/* Call Success Rate Analysis */}
      <div className="mb-8">
        <CallSuccessRateCard timeRange={timeRange} />
      </div>

      <div>
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link to="/phone-numbers" className="block p-6 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-3">📞</span>
              <h3 className="font-semibold text-gray-900 dark:text-white">Buy a Phone Number</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Search for and purchase a new phone number.</p>
          </Link>
          <Link to="/call-forwarding" className="block p-6 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-3">📲</span>
              <h3 className="font-semibold text-gray-900 dark:text-white">Call Forwarding</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Configure call forwarding settings.</p>
          </Link>
          <Link to="/parrots/new" className="block p-6 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-3">🦜</span>
              <h3 className="font-semibold text-gray-900 dark:text-white">Create Parrot</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Create a new AI voice parrot.</p>
          </Link>
          <Link to="/recordings" className="block p-6 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-3">🎙️</span>
              <h3 className="font-semibold text-gray-900 dark:text-white">View Recordings</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Listen to and review your call recordings.</p>
          </Link>
          <Link to="/billing" className="block p-6 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-3">💳</span>
              <h3 className="font-semibold text-gray-900 dark:text-white">Manage Billing</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Update your subscription and payment info.</p>
          </Link>
          <Link to="/knowledge-base" className="block p-6 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-3">📚</span>
              <h3 className="font-semibold text-gray-900 dark:text-white">Knowledge Base</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Upload and manage knowledge files.</p>
          </Link>
        </div>
      </div>
    </div>
  )
}
